import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Button,
  Drawer,
  Typography,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  linearProgressClasses,
  CardActionArea,
  tooltipClasses,
  styled,
  Chip
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import '../../Style/Pulse.css'
import {
  AddComment,
  Check,
  ContactSupport,
  Forum,
  KeyboardDoubleArrowDown
} from '@mui/icons-material'
import RTEView from '../RichTextEditor/RTEView'
import InfoDialog from './InfoDialog'
import PulseAlertMessage from './Helpers/PulseAlertMessage'
import MultipleChoiceComment from './MultipleChoiceComment'

const MultipleChoiceGrid = ({
  section,
  comments = [],
  setComments,
  isAllowedToComment,
  displayName,
  uid,
  document,
  sectionIndex,
  pulseIndex,
  goToNextSection,
  hasNextSection,
  isPreview
}) => {
  const scrollRef = useRef()
  const [pollAnswers, setPollAnswers] = useState(null)
  const [userVoted, setUserVoted] = useState(false)
  const [alert, setAlert] = useState(null)
  const [currentComment, setCurrentComment] = useState({
    message: '',
    messageType: 'Vote',
    uid,
    displayName
  })
  const [isAdmin, setIsAdmin] = useState(false)
  const [sortType, setSortType] = useState('All Comments')
  const [open, setOpen] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [userVotes, setUserVotes] = useState(
    section.isMultipleChoice
      ? section.pulseGrid.rows.map(row => {
          return { label: row, value: null }
        })
      : section.pulseChoice.map(choice => {
          return { label: choice, value: false }
        })
  )
  const [totalVotes, setTotalVotes] = useState(1)

  const toggleDrawer = open => event => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    if (!open) {
      setShowEditModal(false)
    }
    setOpen(open)
  }

  const addComment = (comment, answer) => {
    if (!isAllowedToComment) {
      console.warn('User is not allowed to comment.')
      return
    }
    comment.id = `${uid}-${Date.now()}`
    comment.sectionIndex = sectionIndex
    comment.option = answer
    const d = new Date()
    comment.time = d
    comment.dateString = d.toDateString()
    setComments([...comments, comment], comment)
    setCurrentComment({
      message: '',
      messageType: 'Comment',
      uid,
      displayName
    })
  }
  useEffect(() => {
    setIsAdmin(
      document &&
        (document.uid === uid ||
          (document.admins && document.admins.includes(uid)))
    )
  }, [document, uid])

  useEffect(() => {
    let commentsArray = comments.filter(
      comment =>
        comment.sectionIndex === sectionIndex &&
        comment.pulseIndex === pulseIndex
    )
    setPollAnswers(
      section.isMultipleChoice
        ? section.pulseGrid.rows.map((row, rowIndex) => {
            let vals = section.pulseGrid.columns.map(col => {
              return {
                option: row + ' - ' + col,
                votes: commentsArray
                  .map(arr => arr.option)
                  .reduce((acc, curr) => {
                    return curr[rowIndex].label === row &&
                      curr[rowIndex].value === col
                      ? acc + 1
                      : acc
                  }, 0)
              }
            })
            return vals
          })
        : section.pulseChoice.map((choice, choiceIndex) => {
            return {
              option: choice,
              votes: commentsArray
                .map(arr => arr.option)
                .reduce((acc, curr) => {
                  return curr[choiceIndex].label === choice &&
                    curr[choiceIndex].value
                    ? acc + 1
                    : acc
                }, 0)
            }
          })
    )
    setUserVoted(
      commentsArray.find(comment => comment.uid === uid)?.option || null
    )
    if (!section.isMultipleChoice) {
      setTotalVotes(commentsArray.length)
    }
  }, [comments])

  const saveVote = () => {
    setTimeout(() => {
      if (!isAllowedToComment) {
        return
      }
      if (userVoted) {
        setAlert(
          'You have already responded, please remove your response to respond differently.'
        )
        return
      }
      setAlert(null)
      addComment(currentComment, userVotes)
      setUserVoted(userVotes)
      setSortType('All Comments')
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 250)
    }, 250)
  }

  const showAlert = () => {
    if (userVoted) {
      setAlert(
        'You have already responded, please remove your response to respond differently.'
      )
      return
    }
  }

  const handleChange = (index, value) => {
    setUserVotes(
      section.isMultipleChoice
        ? userVotes.map((row, rowIndex) => {
            if (index === rowIndex) {
              return {
                ...row,
                value: value
              }
            }
            return row
          })
        : userVotes.map((choice, choiceIndex) => {
            if (index === choiceIndex) {
              return {
                ...choice,
                value: !choice.value
              }
            }
            return choice
          })
    )
  }

  const NoMaxWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 'none'
    }
  })

  const VotingSection = ({}) => {
    return (
      <Stack
        spacing={{ xs: 1, md: 2 }}
        direction='column'
        justifyContent='center'
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          pb: { xs: 1, md: 2 },
          opacity:
            (isAdmin && isPreview && !isAllowedToComment) || !isAllowedToComment
              ? 0.6
              : 1,
          pointerEvents:
            ((isAdmin && isPreview && !isAllowedToComment) ||
              !isAllowedToComment) &&
            'none'
        }}
        onClick={
          (isAdmin && isPreview && !isAllowedToComment) || !isAllowedToComment
            ? e => {
                e.stopPropagation()
              }
            : null
        }
      >
        {section.isMultipleChoice
          ? section.pulseGrid.rows.map((row, rowIndex) => (
              <Card
                raised
                key={sectionIndex + 'section_' + rowIndex + 'row_MCGRID'}
                sx={{ width: '100%' }}
              >
                <CardContent>
                  <FormControl sx={{ width: '100%' }}>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      sx={{
                        flexWrap: 'wrap',
                        pb: { xs: 1, md: 2 },
                        width: '100%'
                      }}
                    >
                      <FormLabel
                        id={sectionIndex + 'section_' + rowIndex + 'row_MCGRID'}
                        sx={{ color: 'unset', width: '100%' }}
                      >
                        <RTEView content={row} />
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby={
                          sectionIndex + 'section_' + rowIndex + 'row_MCGRID'
                        }
                        sx={{
                          justifySelf: 'flex-end',
                          ml: 'auto',
                          rowGap: 2,
                          overflowWrap: 'anywhere'
                        }}
                        value={userVotes[rowIndex].value}
                        onChange={(_e, value) => handleChange(rowIndex, value)}
                      >
                        {section.pulseGrid.columns.map((col, colIndex) => (
                          <FormControlLabel
                            key={
                              sectionIndex +
                              'section_' +
                              rowIndex +
                              'row_' +
                              colIndex +
                              '_colMCGRID'
                            }
                            value={col}
                            control={<Radio />}
                            label={col}
                            sx={{ width: '100%' }}
                          />
                        ))}
                      </RadioGroup>
                    </Stack>
                  </FormControl>
                </CardContent>
              </Card>
            ))
          : section.pulseChoice.map((choice, choiceIndex) => (
              <Card
                raised
                key={sectionIndex + 'section_' + choiceIndex + 'choice_MCGRID'}
                sx={{ width: '100%' }}
              >
                <CardActionArea
                  role='checkbox'
                  sx={{
                    backgroundColor: userVotes[choiceIndex].value
                      ? 'primary.main'
                      : 'default',
                    color: userVotes[choiceIndex].value
                      ? 'primary.contrastText'
                      : 'default'
                  }}
                  onClick={_e => handleChange(choiceIndex)}
                >
                  <CardContent>
                    <Typography variant='span' sx={{ color: 'unset' }}>
                      <RTEView content={choice} />
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
        <Button
          variant='contained'
          disabled={
            (section.isMultipleChoice
              ? userVotes.some(row => !row.value)
              : false) || !isAllowedToComment
          }
          sx={{ width: 'fit-content', ml: 'auto' }}
          onClick={saveVote}
        >
          Save
        </Button>
      </Stack>
    )
  }

  const VotedSection = ({}) => {
    return (
      <Stack
        spacing={2}
        sx={{
          width: 'min(100%, 1000px)',
          justifyContent: 'center',
          flexWrap: { md: 'nowrap', xs: 'wrap' },
          pb: 2,
          pt: 1,
          margin: 'auto !important'
        }}
        onClick={showAlert}
        ref={scrollRef}
      >
        {pollAnswers && (
          <>
            {section.isMultipleChoice
              ? pollAnswers.map((row, rowIndex) => (
                  <Card
                    raised
                    key={
                      sectionIndex + 'section_' + rowIndex + 'row_MCGRIDVote'
                    }
                    sx={{ width: '100%' }}
                  >
                    <CardContent>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        sx={{
                          flexWrap: 'wrap',
                          pb: { xs: 1, md: 2 },
                          width: '100%',
                          overflowWrap: 'anywhere'
                        }}
                      >
                        <Typography
                          type='span'
                          sx={{ color: 'unset', width: '100%' }}
                        >
                          <RTEView content={section.pulseGrid.rows[rowIndex]} />
                        </Typography>
                        <Stack
                          spacing={2}
                          sx={{
                            width: '100%',
                            justifyContent: 'center',
                            flexWrap: { md: 'nowrap', xs: 'wrap' },
                            pb: 2,
                            pt: 1
                          }}
                        >
                          {row.map((option, colIndex) => (
                            <Stack
                              tabIndex={0}
                              key={
                                sectionIndex +
                                'section_' +
                                rowIndex +
                                'row_' +
                                colIndex +
                                '_colMCGRIDVote'
                              }
                              aria-labelledby={
                                sectionIndex +
                                'section_' +
                                rowIndex +
                                'row_' +
                                colIndex +
                                '_colMCGRIDVote2'
                              }
                            >
                              <Stack
                                direction={'row'}
                                justifyContent='space-between'
                                alignItems='baseline'
                                sx={{ flexWrap: 'wrap' }}
                                columnGap={2}
                                id={
                                  sectionIndex +
                                  'section_' +
                                  rowIndex +
                                  'row_' +
                                  colIndex +
                                  '_colMCGRIDVote2'
                                }
                              >
                                {section.pulseGrid.columns[colIndex]}
                                <Typography variant='span' sx={{ ml: 'auto' }}>
                                  {userVoted &&
                                    userVoted[rowIndex].value ===
                                      section.pulseGrid.columns[colIndex] && (
                                      <Check
                                        sx={{
                                          top: '6px',
                                          position: 'relative'
                                        }}
                                      />
                                    )}
                                  {option.votes +
                                    ' response' +
                                    (option.votes === 1 ? '' : 's') +
                                    (row.reduce(
                                      (acc, curr) => acc + curr.votes,
                                      0
                                    ) > 0
                                      ? ' (' +
                                        Math.floor(
                                          (option.votes /
                                            row.reduce(
                                              (acc, curr) => acc + curr.votes,
                                              0
                                            )) *
                                            100
                                        ) +
                                        '%)'
                                      : '')}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant='determinate'
                                sx={{
                                  width: '100%',
                                  height: '40px',
                                  borderRadius: '4px',
                                  [`&.${linearProgressClasses.colorPrimary}`]: {
                                    backgroundColor: '#d3d3d387'
                                  },
                                  [`& .${linearProgressClasses.bar}`]: {
                                    backgroundColor:
                                      userVoted &&
                                      userVoted[rowIndex].value ===
                                        section.pulseGrid.columns[colIndex]
                                        ? undefined
                                        : 'lightsteelblue'
                                  }
                                }}
                                className={userVoted ? '' : 'pulse-progress'}
                                value={
                                  userVoted || isAdmin
                                    ? (option.votes /
                                        row.reduce(
                                          (acc, curr) => acc + curr.votes,
                                          0
                                        )) *
                                        100 || -1
                                    : -1
                                }
                              />
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              : section.pulseChoice.map((choice, choiceIndex) => (
                  <NoMaxWidthTooltip
                    title={<RTEView content={choice} />}
                    placement='top-start'
                    key={sectionIndex + 'section_' + choiceIndex + '_colMCVote'}
                  >
                    <Stack
                      tabIndex={0}
                      aria-labelledby={
                        sectionIndex + 'section_' + choiceIndex + '_colMCVote2'
                      }
                      sx={{ width: '100%' }}
                    >
                      <Stack
                        direction={'row'}
                        justifyContent='space-between'
                        alignItems='baseline'
                        sx={{ flexWrap: 'wrap', width: '100%' }}
                        columnGap={2}
                        id={
                          sectionIndex +
                          'section_' +
                          choiceIndex +
                          '_colMCVote2'
                        }
                      >
                        <Typography
                          type='span'
                          sx={{ color: 'unset', width: '100%' }}
                        >
                          <RTEView content={choice} cut />
                        </Typography>
                        <Typography variant='span' sx={{ ml: 'auto' }}>
                          {userVoted && userVoted[choiceIndex].value && (
                            <Check
                              sx={{
                                top: '6px',
                                position: 'relative'
                              }}
                            />
                          )}
                          {pollAnswers[choiceIndex].votes +
                            ' response' +
                            (pollAnswers[choiceIndex].votes === 1 ? '' : 's') +
                            ' (' +
                            Math.floor(
                              (pollAnswers[choiceIndex].votes /
                                (totalVotes ? totalVotes : 1)) *
                                100
                            ) +
                            '%)'}
                        </Typography>
                      </Stack>

                      <LinearProgress
                        variant='determinate'
                        sx={{
                          width: '100%',
                          height: '40px',
                          borderRadius: '4px',
                          [`&.${linearProgressClasses.colorPrimary}`]: {
                            backgroundColor: '#d3d3d387'
                          },
                          [`& .${linearProgressClasses.bar}`]: {
                            backgroundColor:
                              userVoted && userVoted[choiceIndex].value
                                ? undefined
                                : 'lightsteelblue'
                          }
                        }}
                        className={userVoted ? '' : 'pulse-progress'}
                        value={
                          userVoted || isAdmin
                            ? (pollAnswers[choiceIndex].votes /
                                (totalVotes ? totalVotes : 1)) *
                                100 || -1
                            : -1
                        }
                      />
                    </Stack>
                  </NoMaxWidthTooltip>
                ))}
          </>
        )}
      </Stack>
    )
  }

  return (
    <>
      {alert && (
        <PulseAlertMessage
          setAlert={setAlert}
          setOpen={setOpen}
          setSortType={setSortType}
          alert={alert}
        />
      )}
      {!userVoted && (
        <Stack
          direction='row'
          alignItems={'center'}
          justifyContent='flex-end'
          sx={{ mb: 1, mt: -1 }}
        >
          <Tooltip title='Pulse Section Type'>
            <Chip
              color='secondary'
              label={
                'Multiple Choice' + (section.isMultipleChoice ? ' Grid' : '')
              }
              onClick={() => setShowInfo(true)}
            />
          </Tooltip>
          <Tooltip title='Pulse Usage Directions'>
            <IconButton
              sx={{ placeSelf: 'end' }}
              color='secondary'
              onClick={() => setShowInfo(true)}
            >
              <ContactSupport />
            </IconButton>
          </Tooltip>
          <InfoDialog
            showInfo={showInfo}
            setShowInfo={setShowInfo}
            showSpecificSection={
              section.isMultipleChoice ? 'MultipleChoice' : 'MC'
            }
          />
        </Stack>
      )}
      {!userVoted && <VotingSection />}
      {(userVoted || (isAdmin && !isPreview)) && (
        <>
          {pollAnswers && <VotedSection />}
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            flexWrap={'wrap'}
          >
            <Stack
              direction='row'
              justifyContent='flex-end'
              gap={1}
              sx={{ ml: 'auto' }}
              flexWrap={'wrap'}
            >
              <Tooltip title='View All Responses'>
                <Button
                  onClick={() => {
                    setSortType('All Comments')
                    setShowEditModal(false)
                    setOpen(true)
                  }}
                >
                  <Forum />
                </Button>
              </Tooltip>
              {userVoted && (
                <Tooltip title='Add or Edit Your Response'>
                  <Button
                    onClick={() => {
                      setSortType('Yours')
                      setOpen(true)
                      setShowEditModal(true)
                    }}
                    variant='contained'
                  >
                    <AddComment />
                  </Button>
                </Tooltip>
              )}

              {hasNextSection(section) && (
                <Tooltip title='Next Section'>
                  <Button onClick={() => goToNextSection(section)}>
                    <KeyboardDoubleArrowDown />
                  </Button>
                </Tooltip>
              )}
              <Drawer
                open={open}
                onClose={toggleDrawer(false)}
                anchor='right'
                sx={{
                  overflow: 'visible',
                  '& .MuiDrawer-paper': {
                    maxWidth: { xs: '100%', md: '50%' },
                    minWidth: { xs: '100%', md: '50%' }
                  }
                }}
              >
                <MultipleChoiceComment
                  comments={comments}
                  setComments={setComments}
                  pulseValues={[]}
                  isAllowedToComment={isAllowedToComment}
                  uid={uid}
                  isAdmin={isAdmin}
                  document={document}
                  sectionIndex={sectionIndex}
                  sortType={sortType}
                  setSortType={setSortType}
                  prompt={section.title}
                  setAlert={setAlert}
                  toggleDrawer={setOpen}
                  showEditModal={showEditModal}
                  isMultipleChoice={section.isMultipleChoice}
                />
              </Drawer>
            </Stack>
          </Stack>
        </>
      )}
    </>
  )
}

export default MultipleChoiceGrid
